import { useMemo, useRef, useEffect } from 'react';
import Table from '@antimatterhq/ui/components/table';
import { useTableColumns } from '@antimatterhq/ui/hooks/table/useTableColumns';
import { useTableRowSelection } from '@antimatterhq/ui/hooks/table/useTableRowSelection';
import { useVirtuosoTableRows } from '@antimatterhq/ui/hooks/table/useVirtuosoTableRows';
import { CustomCellProps } from '@antimatterhq/ui/hooks/table/useTableRows';
import { TABLE_SELECT_COLUMN_ID } from '@antimatterhq/ui/constants/table';
import type { TableVirtuosoHandle } from 'react-virtuoso';
import { useAtomValue } from 'jotai';
import { QueryResultsDrawer } from '../queryResultsDrawer/QueryResultsDrawer';
import { QueryTableFieldList } from './QueryTableFieldList';
import { QueryResultsTableContextMenu } from './QueryResultsTableContextMenu';
import { QueryResultsTableData } from '@/pages/explore/query/_partials/queryResultsTable/QueryResultsTableData';
import { QueryResultsTableContextMenuCell } from '@/pages/explore/query/_partials/queryResultsTable/QueryResultsTableContextMenuCell';
import { useQueryResults } from '@/hooks/components/query/useQueryResults';
import { activeQueryTabIdAtom } from '@/atoms/query/queryAtoms';

interface QueryResultsVirtualizedTableProps {
  isRunningQuery?: boolean;
  onRowClick: (row: QueryResultsTableData) => void;
}

export function QueryResultsVirtualizedTableContent({
  isRunningQuery,
  onRowClick,
}: QueryResultsVirtualizedTableProps) {
  const activeTabId = useAtomValue(activeQueryTabIdAtom);
  const virtuosoRef = useRef<TableVirtuosoHandle>(null);

  // Get bidirectional methods from updated hook
  const {
    rows,
    fetchNextPage,
    fetchPreviousPage,
    isLoadingInitialPage,
    isLoadingNextPage,
    isLoadingPreviousPage,
    firstItemIndex,
    handleRangeChanged,
  } = useQueryResults(activeTabId ?? '');

  const isLoading =
    isRunningQuery ||
    isLoadingInitialPage ||
    isLoadingNextPage ||
    isLoadingPreviousPage;

  // Reset scroll position when rows are cleared (refinements change or zoom)
  useEffect(() => {
    if (rows.length === 0 && virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex(0);
    }
  }, [rows.length]);

  const {
    columns,
    columnVisibility,
    columnOrders,
    columnWidths,
    pinnedColumns,
    toggleColumnVisibility,
    onColumnOrderChange,
    setAllColumnOrders,
  } = useTableColumns();

  const { fixedHeaderContent, itemContent } =
    useVirtuosoTableRows<QueryResultsTableData>({
      columns,
      isLoading,
      'data-testid': 'query-results-table',
      selection: useTableRowSelection(rows),
      customBodyCells: useMemo(() => {
        // Static cells that don’t follow the generic pattern
        const staticMapping: Record<
          string,
          React.ComponentType<CustomCellProps<QueryResultsTableData>>
        > = {
          edit: ({ item }) => (
            <Table.ActionsCell
              data-testid={`actions-cell-${item.id}`}
              actions={[
                {
                  label: 'View Details',
                  icon: 'lucide:eye',
                  onClick: () => {
                    onRowClick(item);
                  },
                },
              ]}
            />
          ),
        };

        const dynamicMapping = Object.fromEntries(
          columns
            .filter((c) => c.id !== 'edit' && c.id !== TABLE_SELECT_COLUMN_ID)
            .map<
              [
                string,
                React.ComponentType<CustomCellProps<QueryResultsTableData>>,
              ]
            >((c) => [c.id, QueryResultsTableContextMenuCell]),
        );

        return {
          ...staticMapping,
          ...dynamicMapping,
        };
      }, [columns, onRowClick]),
    });

  return (
    // 608px is the height of the header, table, and field list
    <div
      className="flex h-[calc(100vh-608px)] min-h-[300px] gap-2"
      data-testid="query-results-table"
    >
      <div className="w-64 flex-shrink-0">
        <QueryTableFieldList
          columnVisibility={columnVisibility}
          columnOrders={columnOrders}
          pinnedColumns={pinnedColumns}
          toggleColumnVisibility={toggleColumnVisibility}
          onColumnOrderChange={onColumnOrderChange}
          setAllColumnOrders={setAllColumnOrders}
          className="flex h-full flex-col"
        />
      </div>

      <div className="relative flex-1">
        <Table.StyleProvider
          virtualized={true}
          pageSize={undefined}
          columnWidths={columnWidths}
          columnVisibility={columnVisibility}
          data-testid="query-results-table-style-provider"
        >
          <Table.Virtual
            atBottomThreshold={0}
            atTopThreshold={0}
            columns={columns}
            data={rows}
            emptyMessage="No results found"
            firstItemIndex={firstItemIndex}
            fixedHeaderContent={fixedHeaderContent}
            handleEndReached={fetchNextPage}
            handleStartReached={fetchPreviousPage}
            isLoading={isLoading}
            itemContent={itemContent}
            onRowClick={onRowClick}
            virtuosoRef={virtuosoRef}
            handleRangeChanged={handleRangeChanged}
          />
        </Table.StyleProvider>
      </div>
      <QueryResultsDrawer
        toggleColumnVisibility={toggleColumnVisibility}
        columnVisibility={columnVisibility}
      />
      <QueryResultsTableContextMenu />
    </div>
  );
}
